import {
  AppInsightsContext,
  AppInsightsErrorBoundary,
  ReactPlugin,
  useAppInsightsContext,
  useTrackEvent as useAITrackEvent,
  useTrackMetric as useAITrackMetric,
  withAITracking,
} from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import React, { useEffect } from 'react';
import Config from './TelemetryConfig';

const reactPlugin = new ReactPlugin();
const appInsights = new ApplicationInsights({
  config: {
    connectionString: Config.AppInsightsConnectionString,
    // track page views/route changes
    enableAutoRouteTracking: true,
    // enable distributed tracing
    enableCorsCorrelation: true,
    enableRequestHeaderTracking: true,
    enableResponseHeaderTracking: true,
    extensions: [reactPlugin],
  },
});

if (process.env['NODE_ENV'] === 'production') {
  appInsights.loadAppInsights();
}

export function useSyncTelemetryUser(userId?: string, tenantId?: string) {
  useEffect(() => {
    if (userId && tenantId) {
      appInsights.setAuthenticatedUserContext(userId, tenantId);
    }
  }, [userId, tenantId]);
}

/**
 * HOC that adds telemetry for specific component
 * @param comp Component to track
 * @returns Wire up component
 */
export function withTelemetry<T>(comp: React.ComponentType<T>) {
  return withAITracking(reactPlugin, comp);
}

/**
 * Hook form of `withTelemetry`
 *
 * @see https://docs.microsoft.com/en-us/azure/azure-monitor/app/javascript-react-plugin#usetrackmetric
 *
 * @param componentName
 * @param customProps additional data you want tracked
 * @returns Function to manually call to trigger a tracked event within the component
 */
export const useTrackComponent = (
  componentName: string,
  customProps?: Record<string, unknown>
) => {
  const context = useAppInsightsContext();
  return useAITrackMetric(context, componentName, customProps);
};

/**
 * @see https://docs.microsoft.com/en-us/azure/azure-monitor/app/javascript-react-plugin#usetrackevent
 */
export function useTrackEvent<T>(
  eventName: string,
  eventData: T,
  skipFirstRun?: boolean
) {
  const context = useAppInsightsContext();
  return useAITrackEvent(context, eventName, eventData, skipFirstRun);
}

type TelemetryProviderProps = {};
/**
 * Telemetry provider for entire application
 */
export const TelemetryProvider: React.FC<TelemetryProviderProps> = ({
  children,
}) => {
  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      {children}
    </AppInsightsContext.Provider>
  );
};

type TelemetryErrorBoundaryProps = {
  errorComp: React.ComponentType<unknown>;
  children: React.ReactElement;
};
/**
 * @see https://docs.microsoft.com/en-us/azure/azure-monitor/app/javascript-react-plugin#react-error-boundaries
 */
export const TelemetryErrorBoundary: React.FC<TelemetryErrorBoundaryProps> = ({
  children,
  errorComp,
}) => {
  return (
    <AppInsightsErrorBoundary onError={errorComp} appInsights={reactPlugin}>
      {children}
    </AppInsightsErrorBoundary>
  );
};

/**
 * Track an error to Application Insights
 * Use this to manually log errors that are caught by error boundaries
 *
 * @param error - The error object
 * @param properties - Additional context (component name, user actions, etc.)
 * @param measurements - Numeric data (error count, time to error, etc.)
 *
 * @example
 * trackError(error, {
 *   boundaryName: 'TaskDetails',
 *   componentStack: errorInfo.componentStack,
 *   url: window.location.href
 * });
 */
export function trackError(
  error: Error,
  properties?: Record<string, string>,
  measurements?: Record<string, number>
) {
  if (process.env['NODE_ENV'] === 'production') {
    appInsights.trackException({
      exception: error,
      properties,
      measurements,
      severityLevel: 3, // Error level (0=Verbose, 1=Info, 2=Warning, 3=Error, 4=Critical)
    });
  }

  // eslint-disable-next-line no-console
  console.error('Error tracked to Application Insights:', {
    error: error.message,
    stack: error.stack,
    ...properties,
    ...measurements,
  });
}

/**
 * Get the Application Insights instance
 */
export function getAppInsights() {
  return appInsights;
}
